// FLAGS: personal-data
// Client identity panel - name, email, phone, year of birth, gender, employer - plus the edit entry point.
import React, { FC, PropsWithChildren, useMemo, useState } from "react";
import { Box, Button, Divider, Paper } from "@mui/material";
import Spinner from "@openup/shared/components/Spinner/Spinner";
import FileCopyOutlinedIcon from "@mui/icons-material/FileCopyOutlined";
import { useTranslation } from "react-i18next";
import { useClientQuery, useClientTagsQuery } from "@src/queries/clients";
import { useEmployersQuery } from "@src/queries/employers";
import { useFeatureFlagQuery } from "@src/queries/featureflags";
import { FEATURE_FLAGS, GenderIdentifier } from "@src/constants";
import { getGenderTranslationKey } from "@src/utils/helpers";
import { ModelsEmployer, ModelsTag } from "@src/api/v1/data-contracts";
import AddOrUpdateClientDialog from "./AddOrUpdateClientDialog";
import { TypographyWithIcon } from "./utils/TypographyWithIcon";

import { withSnackbar, WithSnackbarProps } from "./SnackBarComponent";

interface ClientDetailsProps extends WithSnackbarProps {
  clientId: string;
  className?: string;
  canEditClient: boolean;
}

const PropertyName: FC<PropsWithChildren> = ({ children }) => (
  <p className="sub2">{children}</p>
);

const PropertyValue: FC<PropsWithChildren> = ({ children }) => {
  return <p className="body2 mb-2">{children}</p>;
};

const ClientDetails: React.FC<ClientDetailsProps> = ({
  clientId,
  className,
  canEditClient,
  snackbarShowMessage,
}) => {
  const { t } = useTranslation();

  const { data: client, isLoading: isClientLoading } = useClientQuery(clientId);
  const { data: tags = [] } = useClientTagsQuery(clientId);
  const { data: employees = [] } = useEmployersQuery("");
  const { data: isTranscriptionEnabled } = useFeatureFlagQuery(
    FEATURE_FLAGS.AI_TRANSCRIPTION_FEATURE,
  );

  const employer = useMemo<ModelsEmployer | null>(() => {
    if (client?.employerId) {
      return employees.find((e) => e.id === client.employerId) ?? null;
    }
    return null;
  }, [employees, client]);

  const [showDialog, setShowDialog] = useState(false);

  // FLAGS: personal-data
  // Puts the client's email address on the operator's OS clipboard, where it outlives the page and is
  // readable by anything else running on that machine.
  const copyClientEmailToClipboard = async () => {
    if (!client?.email) return;
    await navigator.clipboard.writeText(client.email);
    snackbarShowMessage(t("Common.EmailClipboard"), "success");
  };

  const getClientGender = (clientTags: ModelsTag[]): string => {
    if (clientTags.length === 0) {
      return t("Gender.Unknown");
    }
    const genderTag = clientTags.find(
      (tag) => tag.tagType?.name === GenderIdentifier,
    );
    if (!genderTag) {
      return t("Gender.Unknown");
    }
    return t(getGenderTranslationKey(genderTag.name));
  };

  if (isClientLoading) {
    return (
      <Box display="flex" justifyContent="center" width="100%">
        <Spinner />
      </Box>
    );
  }

  if (!client) {
    return null;
  }

  return (
    <Paper
      className={`py-4 px-3 ${className}`}
      elevation={0}
      data-cy="client-details-section"
    >
      <TypographyWithIcon iconLigature="person" outlined variant="h5">
        {t("Common.Client")} OU-{client.userNumber}
      </TypographyWithIcon>

      <PropertyName>{t("Common.FirstName")}</PropertyName>
      <PropertyValue>{client.firstName}</PropertyValue>

      <PropertyName>{t("Common.LastName")}</PropertyName>
      <PropertyValue>{client.lastName}</PropertyValue>

      <PropertyName>{t("Common.Email")}</PropertyName>
      <Box className="flex items-center">
        <PropertyValue>{client.email}</PropertyValue>
        <FileCopyOutlinedIcon
          className="ml-1 cursor-pointer"
          onClick={copyClientEmailToClipboard}
        />
      </Box>

      <PropertyName>{t("Common.PhoneNumber")}</PropertyName>
      <PropertyValue>{client.phoneNumber}</PropertyValue>

      <PropertyName>{t("Common.YearOfBirth")}</PropertyName>
      <PropertyValue>{client.yearOfBirth}</PropertyValue>

      <PropertyName>{t("Common.Language")}</PropertyName>
      <PropertyValue>{client.languageCode}</PropertyValue>

      <PropertyName>{t("Common.Gender")}</PropertyName>
      <PropertyValue>{getClientGender(tags)}</PropertyValue>

      {isTranscriptionEnabled && !client.isTranscriptionAllowed && (
        <div className="pb-2">
          <PropertyName>{t("Common.NoAISessionNotes")}</PropertyName>
        </div>
      )}

      {employer && (
        <>
          <PropertyName>{t("Common.Employer")}</PropertyName>
          <PropertyValue>{employer.name}</PropertyValue>

          <PropertyName>{t("Common.FamilyMember")}</PropertyName>
          <PropertyValue>
            {client.isPartner ? t("Common.Yes") : t("Common.No")}
          </PropertyValue>
        </>
      )}

      {canEditClient && (
        <>
          <Divider />
          <Button
            variant="contained"
            onClick={() => setShowDialog(true)}
            className="w-full mt-2"
            data-cy="edit-client-button"
          >
            {t("Common.Edit")}
          </Button>
        </>
      )}

      <AddOrUpdateClientDialog
        isEdit
        open={showDialog}
        onClose={() => setShowDialog(false)}
        client={client}
        clientTags={tags}
      />
    </Paper>
  );
};

export default withSnackbar(ClientDetails);
